import { z } from 'zod';
// Formas de pagamento: as mesmas das contratações.
import { IT_PAYMENT_METHODS } from './it.ts';

/**
 * TI, Financeiro (Fase 3 revisada): serviços contratados, cobranças e conferência de faturas. A TI
 * confere o que foi cobrado contra o contratado; nunca paga nem lança nota (Contas a Pagar).
 */

export const IT_SERVICE_KINDS = [
  'internet',
  'telephony',
  'cloud',
  'hosting',
  'support',
  'outsourcing',
  'equipment_rental',
  'other',
] as const;
export const IT_SERVICE_STATUSES = ['active', 'suspended', 'cancelled'] as const;
export const IT_CHARGE_KINDS = ['fixed', 'unit', 'consumption'] as const;
export const IT_CHARGE_PERIODS = ['monthly', 'quarterly', 'semiannual', 'annual'] as const;
export const IT_ADJUSTMENT_INDEXES = ['ipca', 'igpm', 'inpc', 'usd', 'none', 'other'] as const;
export const IT_INVOICE_STATUSES = [
  'received',
  'checked',
  'divergent',
  'awaiting_attest',
  'attested',
  'sent',
  'paid',
  'contested',
  'cancelled',
] as const;
export type ItInvoiceStatus = (typeof IT_INVOICE_STATUSES)[number];

/** Divergências que a conferência aponta (o texto vem das traduções). */
export const IT_INVOICE_DIVERGENCES = [
  'line_without_origin',
  'quantity_above_contract',
  'price_differs',
  'charged_after_cancellation',
  'consumption_above_limit',
  'total_differs_from_lines',
  'billed_to_other_company',
  'outside_contract_period',
] as const;
export type ItInvoiceDivergence = (typeof IT_INVOICE_DIVERGENCES)[number];

/** Meses de cada periodicidade (apropriação e conferência). */
export const IT_PERIOD_MONTHS: Record<(typeof IT_CHARGE_PERIODS)[number], number> = {
  monthly: 1,
  quarterly: 3,
  semiannual: 6,
  annual: 12,
};

const id = z.uuid().nullable().optional();
const day = z.iso.date();
const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? undefined : v))
    .optional();
const amount = z
  .string()
  .trim()
  .refine((v) => !v.startsWith('-'), 'O valor não pode ser negativo')
  .refine(
    (v) => v.startsWith('-') || /^\d{1,15}(\.\d{1,4})?$/.test(v),
    'Use números com ponto (ex.: 1500.00)',
  );
const currency = z.string().regex(/^[A-Z]{3}$/, 'Escolha a moeda');
// Referência de pagamento sem sequências longas de dígitos (nada de cartão nem conta).
const reference = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .refine((v) => !/[0-9]{9,}/.test(v), 'Não informe números de cartão nem de conta');

export const itServiceInput = z
  .object({
    legalEntityId: z.uuid(),
    branchId: id,
    kind: z.enum(IT_SERVICE_KINDS),
    name: z.string().trim().min(2).max(200),
    partnerId: z.uuid(),
    currency,
    startDate: day,
    endDate: day.nullable().optional(),
    renewalDate: day.nullable().optional(),
    noticeDays: z.number().int().min(0).max(365).nullable().optional(),
    autoRenewal: z.boolean().default(false),
    ownerUserId: id,
    departmentId: id,
    costCenterId: id,
    contractId: id,
    purchaseRequestId: id,
    assetId: id,
    paymentMethod: z.enum(IT_PAYMENT_METHODS).nullable().optional(),
    notes: text(4000),
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'O fim não pode ser antes do início',
  });
export type ItServiceInput = z.infer<typeof itServiceInput>;

export const itServiceChargeInput = z
  .object({
    kind: z.enum(IT_CHARGE_KINDS),
    description: z.string().trim().min(2).max(200),
    quantity: amount.nullable().optional(),
    unitAmount: amount.nullable().optional(),
    consumptionLimit: amount.nullable().optional(),
    billingPeriod: z.enum(IT_CHARGE_PERIODS),
    validFrom: day,
    validUntil: day.nullable().optional(),
    adjustmentIndex: z.enum(IT_ADJUSTMENT_INDEXES).nullable().optional(),
    adjustmentBaseDate: day.nullable().optional(),
  })
  .refine((v) => v.kind === 'consumption' || (!!v.quantity && !!v.unitAmount), {
    path: ['unitAmount'],
    message: 'Informe a quantidade e o valor',
  })
  .refine((v) => !v.validUntil || v.validUntil >= v.validFrom, {
    path: ['validUntil'],
    message: 'O fim não pode ser antes do início',
  });
export type ItServiceChargeInput = z.infer<typeof itServiceChargeInput>;

/** Reajuste: a cobrança atual termina no dia anterior e uma nova, com o valor novo, começa. */
export const itChargeAdjustInput = z.object({
  validFrom: day,
  unitAmount: amount,
  quantity: amount.nullable().optional(),
  adjustmentIndex: z.enum(IT_ADJUSTMENT_INDEXES).nullable().optional(),
  adjustmentBaseDate: day.nullable().optional(),
});

export const itServiceCancelInput = z.object({
  effectiveOn: day,
  reason: z.string().trim().min(3, 'Explique o motivo').max(1000),
});

const invoiceLine = z
  .object({
    subscriptionId: id,
    entitlementId: id,
    serviceId: id,
    serviceChargeId: id,
    description: z.string().trim().min(1).max(300),
    quantity: amount.default('1'),
    unitAmount: amount,
  })
  .refine((v) => !(v.subscriptionId && v.serviceId), {
    path: ['serviceId'],
    message: 'Cada item é de uma contratação ou de um serviço',
  });

export const itInvoiceInput = z
  .object({
    legalEntityId: z.uuid(),
    branchId: id,
    partnerId: z.uuid(),
    number: z.string().trim().min(1).max(60),
    issueDate: day,
    dueDate: day.nullable().optional(),
    competence: z.string().regex(/^\d{4}-\d{2}$/, 'Informe o mês (aaaa-mm)'),
    periodFrom: day.nullable().optional(),
    periodTo: day.nullable().optional(),
    currency,
    totalAmount: amount,
    taxesAmount: amount.nullable().optional(),
    paymentMethod: z.enum(IT_PAYMENT_METHODS).nullable().optional(),
    billedEntityId: id,
    chargeDate: day.nullable().optional(),
    payablesInvoiceId: id,
    // Centro que responde pela fatura: o gestor dele atesta (motor de alçadas).
    costCenterId: id,
    lines: z.array(invoiceLine).min(1, 'Inclua pelo menos um item').max(200),
  })
  .refine((v) => !v.periodTo || !v.periodFrom || v.periodTo >= v.periodFrom, {
    path: ['periodTo'],
    message: 'O fim do período não pode ser antes do início',
  });
export type ItInvoiceInput = z.infer<typeof itInvoiceInput>;

/** Mudança de situação da fatura (contestação, cancelamento, envio ao Financeiro, pagamento). */
export const itInvoiceStatusInput = z.object({
  note: text(2000),
});
export const itInvoiceSendInput = z.object({ sentOn: day, note: text(2000) });
export const itInvoicePaidInput = z.object({ paidOn: day, paymentReference: reference });
